// src/components/results/ResultPage.tsx
import { motion } from 'framer-motion'
import type { Tier, TierCopy } from '../../lib/tiers'
import { MOOD_BRAIN_COPY } from '../../lib/tiers'
import type { ScoringResult } from '../../lib/scoring'
import type { RedFlagResult } from '../../lib/redflags'
import type { Insight } from '../../lib/insights'
import { PAInTGrid } from './PAInTGrid'
import { InsightCards } from './InsightCards'

interface ResultPageProps {
  tier: Tier
  tierCopy: TierCopy
  scores: ScoringResult
  redFlags: RedFlagResult
  insights: Insight[]
  firstName?: string
  contraceptionStatus?: string
  previousDiagnoses?: string[]
}

const BOOKING_URL = 'https://fertility.ifsdnetwork.com'

const NEXT_STEPS = [
  { n: '01', label: 'Book', desc: 'Reserve your FEMM Hormonal Health Review' },
  { n: '02', label: 'Consultation', desc: 'Dr. Nyandia arrives having already reviewed your results' },
  { n: '03', label: 'Your plan', desc: 'A named, investigated, medically managed starting point' },
]

const isPreview = import.meta.env.VITE_IS_PREVIEW === 'true'

// ─── Shared style tokens ───────────────────────────────────

// Section eyebrow — JetBrains Mono, muted, caps. Vermilion is NOT used here;
// the CTA is the single vermilion moment per layout.
const eyebrow: React.CSSProperties = {
  fontFamily: 'var(--th-mono)',
  fontSize: '10.5px',
  fontWeight: 500,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: 'var(--th-muted)',
  display: 'block',
  marginBottom: '10px',
}

// Card — bone bg, 1px ink border, 26/24/28 padding per found-card spec
const card: React.CSSProperties = {
  background: 'var(--th-bone)',
  border: '1px solid var(--th-ink)',
  padding: '26px 24px 28px',
}

// Section heading — Fraunces 400, 32px / 1.1 / -0.02em per Heading scale
const sectionH3: React.CSSProperties = {
  fontFamily: 'var(--th-serif)',
  fontWeight: 400,
  fontSize: '32px',
  lineHeight: 1.1,
  letterSpacing: '-0.02em',
  color: 'var(--th-ink)',
  marginBottom: '12px',
}

// Body text — Inter Tight 400, 15.5px / 1.75
const bodyText: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: '15.5px',
  fontWeight: 400,
  color: 'var(--th-ink-2)',
  lineHeight: 1.75,
}

// Muted caption
const mutedText: React.CSSProperties = {
  fontFamily: 'var(--font-sans)',
  fontSize: '13px',
  color: 'var(--th-muted)',
  lineHeight: 1.6,
}

// 28px gap between sections (gutter spacing from the scale)
const sectionGap: React.CSSProperties = { marginTop: '28px' }

export function ResultPage({
  tier,
  tierCopy,
  scores,
  redFlags,
  insights,
  firstName: _firstName,
  previousDiagnoses,
}: ResultPageProps) {
  const showMoodPanel = scores.mood >= 8
  const isUrgent = tier.number >= 3

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: 'My FEMM Hormonal Health Results',
        text: `I just completed the FEMM Symptom Self-Assessment with Dr. Nyandia. My result: ${tier.name}. ${tierCopy.headline}`,
        url: window.location.href,
      }).catch(() => {})
    } else {
      const text = `I just completed the FEMM Symptom Self-Assessment with Dr. Nyandia. My result: ${tier.name}. Take yours at ${window.location.origin}`
      window.open(`https://wa.me/?text=${encodeURIComponent(text)}`, '_blank')
    }
  }

  return (
    <div style={{ background: 'var(--th-stone)', minHeight: '100dvh' }}>

      {/* ── Hero ────────────────────────────────────────── */}
      <div style={{ background: 'var(--th-ink)' }}>
        <div style={{
          maxWidth: '640px',
          margin: '0 auto',
          padding: '60px 24px 70px',
        }}>

          {/* Letterhead bar — matches .mast-top pattern */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.4 }}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-start',
              paddingBottom: '26px',
              borderBottom: '1px solid rgba(201,200,193,0.18)',
              marginBottom: '48px',
            }}
          >
            <a
              href="https://ifsdnetwork.com/"
              target="_blank"
              rel="noopener noreferrer"
              style={{ display: 'inline-flex', alignItems: 'center', gap: '0.6rem', textDecoration: 'none' }}
            >
              <img src="/logo.png" alt="IFSD" style={{ height: '26px', width: 'auto' }} />
              <div>
                <div style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 700,
                  fontSize: '13px',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                  color: 'var(--th-bone)',
                  lineHeight: 1.2,
                }}>
                  IFSD
                </div>
                <div style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 400,
                  fontSize: '11px',
                  letterSpacing: '0.12em',
                  color: 'rgba(246,245,241,0.55)',
                  lineHeight: 1.3,
                }}>
                  Institute for Family &amp; Social Development
                </div>
              </div>
            </a>
            <div style={{
              fontFamily: 'var(--th-mono)',
              fontSize: '10.5px',
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: 'rgba(201,200,193,0.45)',
              textAlign: 'right',
              lineHeight: 1.9,
            }}>
              Dr. Nyandia<br />FEMM Practice
            </div>
          </motion.div>

          {/* Eyebrow — muted on dark */}
          <motion.span
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
            style={{
              fontFamily: 'var(--th-mono)',
              fontSize: '10.5px',
              fontWeight: 500,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'rgba(201,200,193,0.55)',
              display: 'block',
              marginBottom: '20px',
            }}
          >
            Hormonal Health Assessment
          </motion.span>

          {/* Tier chip — .chip-outline adapted for dark bg */}
          <motion.div
            initial={{ opacity: 0, x: -8 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.12 }}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              fontFamily: 'var(--font-sans)',
              fontWeight: 500,
              fontSize: '11px',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              padding: '5px 11px',
              border: '1px solid rgba(246,245,241,0.22)',
              color: 'rgba(246,245,241,0.6)',
              marginBottom: '20px',
            }}
          >
            {tier.name}
          </motion.div>

          {/* Hero headline — Display scale: Fraunces 300, 56px / 0.98 / -0.03em */}
          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.18, duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            style={{
              fontFamily: 'var(--th-serif)',
              fontWeight: 300,
              fontSize: 'clamp(2.5rem, 8vw, 3.5rem)',
              lineHeight: 0.98,
              letterSpacing: '-0.03em',
              color: 'var(--th-bone)',
              maxWidth: '520px',
            }}
          >
            {tierCopy.headline}
          </motion.h1>

          {/* Red flag — uses pine (considered, not the loud vermilion signal) */}
          {redFlags.hasRedFlag && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              style={{
                marginTop: '32px',
                padding: '20px 22px',
                background: 'rgba(42,76,63,0.15)',
                border: '1px solid rgba(42,76,63,0.35)',
                borderLeft: '3px solid var(--th-pine)',
              }}
            >
              <span style={{
                fontFamily: 'var(--th-mono)',
                fontSize: '10.5px',
                fontWeight: 500,
                letterSpacing: '0.18em',
                textTransform: 'uppercase',
                color: 'var(--th-pine)',
                display: 'block',
                marginBottom: '8px',
              }}>
                Priority Attention
              </span>
              {redFlags.reasons.map(r => (
                <p key={r.code} style={{ ...mutedText, color: 'rgba(246,245,241,0.65)', lineHeight: 1.55 }}>
                  — {r.label}
                </p>
              ))}
            </motion.div>
          )}
        </div>
      </div>

      {/* ── Body ──────────────────────────────────────────── */}
      <div style={{
        maxWidth: '640px',
        margin: '0 auto',
        padding: `48px 24px ${isPreview ? 'calc(5rem + 56px)' : '5rem'}`,
      }}>

        {/* Assessment summary */}
        <motion.section
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28 }}
          style={card}
        >
          <span style={eyebrow}>Your Assessment</span>
          {tierCopy.body.split('\n\n').map((para, i, arr) => (
            <p key={i} style={{ ...bodyText, marginBottom: i < arr.length - 1 ? '1rem' : 0 }}>
              {para}
            </p>
          ))}
        </motion.section>

        {/* PAInT profile */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.36 }}
          style={{ ...card, ...sectionGap }}
        >
          <PAInTGrid signals={scores.signals} />
        </motion.div>

        {/* Key insights */}
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.44 }}
          style={{ ...card, ...sectionGap }}
        >
          <InsightCards insights={insights} />
        </motion.div>

        {/* Investigation gap */}
        {scores.investigationGap.length > 0 && (
          <motion.section
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            aria-label="What hasn't been investigated"
            style={{ ...card, ...sectionGap }}
          >
            <span style={eyebrow}>Uninvestigated</span>
            <h3 style={sectionH3}>What hasn't yet been tested</h3>
            <p style={{ ...mutedText, marginBottom: '16px' }}>
              Based on your responses, the following hormonal systems have not been formally tested:
            </p>
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {scores.investigationGap.map((test, i) => (
                <div key={test} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '14px',
                  padding: '11px 0',
                  borderTop: i === 0 ? '1px solid var(--th-ink)' : '1px dashed var(--th-rule)',
                }}>
                  <div style={{ width: '5px', height: '5px', background: 'var(--th-rule)', flexShrink: 0 }} />
                  <span style={bodyText}>{test}</span>
                </div>
              ))}
            </div>
            <p style={{ ...mutedText, marginTop: '16px', fontStyle: 'italic' }}>
              These tests, ordered together and interpreted alongside your FEMM cycle chart, form the basis of a complete hormonal health picture.
            </p>
          </motion.section>
        )}

        {/* Mood-brain panel */}
        {showMoodPanel && (
          <motion.section
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.54 }}
            style={{
              ...card,
              ...sectionGap,
              background: 'var(--th-warm)',
            }}
          >
            <span style={eyebrow}>Mood &amp; Brain</span>
            <h3 style={sectionH3}>{MOOD_BRAIN_COPY.headline}</h3>
            {MOOD_BRAIN_COPY.body.split('\n\n').map((para, i) => (
              <p key={i} style={{ ...bodyText, marginBottom: i === 0 ? '1rem' : 0 }}>
                {para}
              </p>
            ))}
            <p style={{ ...mutedText, marginTop: '12px', fontStyle: 'italic' }}>
              {MOOD_BRAIN_COPY.citation}
            </p>
          </motion.section>
        )}

        {/* Previous diagnoses */}
        {previousDiagnoses && previousDiagnoses.length > 0 && !previousDiagnoses.includes('none') && (
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.58 }}
            style={{
              ...card,
              ...sectionGap,
              borderLeft: '3px solid var(--th-pine)',
            }}
          >
            <span style={{ ...eyebrow, color: 'var(--th-pine)' }}>Existing Diagnoses</span>
            <p style={bodyText}>
              Having a diagnosis doesn't mean the conversation is over. Many women with confirmed conditions have never had FEMM charting, integrated insulin assessment, or a comprehensive hormonal management plan. A FEMM review goes deeper.
            </p>
          </motion.div>
        )}

        {/* Doctor's note — .pull pattern: 80px label col | 1fr blockquote */}
        <motion.section
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.62 }}
          style={{ ...card, ...sectionGap }}
        >
          <span style={eyebrow}>From Dr. Nyandia</span>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '80px 1fr',
            gap: '22px',
            borderTop: '1px solid var(--th-ink)',
            borderBottom: '1px solid var(--th-ink)',
            padding: '26px 0',
          }}>
            {/* Label column */}
            <div style={{
              fontFamily: 'var(--th-mono)',
              fontSize: '10px',
              letterSpacing: '0.14em',
              textTransform: 'uppercase',
              color: 'var(--th-muted)',
              lineHeight: 1.7,
            }}>
              FEMM<br />Practitioner<br />Dr. N. Maina
            </div>
            {/* Blockquote column — Fraunces 300, 23px / 1.3 / -0.015em */}
            <blockquote style={{
              fontFamily: 'var(--th-serif)',
              fontWeight: 300,
              fontSize: '23px',
              lineHeight: 1.3,
              letterSpacing: '-0.015em',
              color: 'var(--th-ink)',
            }}>
              {tierCopy.doctorNote.split('\n\n').map((para, i, arr) => (
                <p key={i} style={{ marginBottom: i < arr.length - 1 ? '0.75em' : 0 }}>
                  {para}
                </p>
              ))}
            </blockquote>
          </div>
        </motion.section>

        {/* Next steps */}
        <motion.section
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.68 }}
          style={{ ...card, ...sectionGap }}
        >
          <span style={eyebrow}>Next Steps</span>
          {NEXT_STEPS.map((step, idx) => (
            <div key={step.n} style={{
              display: 'grid',
              gridTemplateColumns: '52px 1fr',
              borderTop: idx === 0 ? '1px solid var(--th-ink)' : '1px solid var(--th-rule)',
              padding: '14px 0',
            }}>
              <span style={{
                fontFamily: 'var(--th-mono)',
                fontSize: '10.5px',
                letterSpacing: '0.1em',
                color: 'var(--th-muted)',
                fontWeight: 500,
                paddingTop: '2px',
              }}>
                {step.n}
              </span>
              <div>
                <p style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: '15px',
                  color: 'var(--th-ink)',
                  marginBottom: '3px',
                }}>
                  {step.label}
                </p>
                <p style={mutedText}>{step.desc}</p>
              </div>
            </div>
          ))}
        </motion.section>

        {/* Share — secondary button, no card */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.76 }}
          style={{ marginTop: '28px' }}
        >
          <button
            onClick={handleShare}
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 500,
              fontSize: '13px',
              letterSpacing: '0.01em',
              padding: '14px 22px',
              background: 'transparent',
              color: 'var(--th-ink)',
              border: '1px solid var(--th-ink)',
              cursor: 'pointer',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '10px',
              lineHeight: 1,
            }}
          >
            Share results
            <span style={{ fontFamily: 'var(--th-serif)', fontStyle: 'italic', fontWeight: 300 }}>→</span>
          </button>
        </motion.div>
      </div>

      {/* ── Sticky CTA ─────────────────────────────────────
          This is THE single vermilion moment for urgent tiers.
          For non-urgent tiers it is ink (btn-primary).         */}
      <motion.div
        initial={{ y: 80, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ delay: 0.45, type: 'spring', stiffness: 200 }}
        style={{
          position: 'fixed',
          bottom: isPreview ? 48 : 0,
          left: 0,
          right: 0,
          padding: '14px 24px',
          background: 'var(--th-bone)',
          borderTop: '1px solid var(--th-ink)',
          zIndex: 50,
        }}
      >
        <a
          href={BOOKING_URL}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '10px',
            width: '100%',
            maxWidth: '400px',
            margin: '0 auto',
            padding: '14px 22px',
            background: isUrgent ? 'var(--th-vermilion)' : 'var(--th-ink)',
            color: 'var(--th-bone)',
            border: isUrgent ? '1px solid var(--th-vermilion)' : '1px solid var(--th-ink)',
            fontFamily: 'var(--font-sans)',
            fontWeight: 500,
            fontSize: '13px',
            letterSpacing: '0.01em',
            textDecoration: 'none',
            lineHeight: 1,
          }}
        >
          {tierCopy.ctaText}
          <span style={{ fontFamily: 'var(--th-serif)', fontStyle: 'italic', fontWeight: 300 }}>→</span>
        </a>
      </motion.div>
    </div>
  )
}
